import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getUserVerified } from '../../api/tvs/userVerifications';
import { VerificationStatus } from '../../@types/VerificationStatus';
import VerificationStatusCheck from '../../components/verificationStatusCheck/VerificationStatusCheck';
import { VerificationMethod } from '../../@types/VerificationMethod';
import { trackEvent } from '../../tracking/tracking';
import { useClientConfig } from '../../utils/clientConfigContext';

const POLLING_INTERVAL_MS = 1000;
const MAX_VERIFICATION_TIME_MS = 5000;
const PROGRESS_INCREMENT = 20;
const MAX_PROGRESS_WHILE_CHECKING = 100;
const COMPLETION_DELAY_MS = 1000; // 2 seconds delay to show 100% progress

interface WebIdStatusCheckPageProps {
  onVerificationComplete?: (status: VerificationStatus) => void;
}

const WebIdStatusCheckPage: React.FC<WebIdStatusCheckPageProps> = ({ onVerificationComplete }) => {
  const [progress, setProgress] = useState(0);
  const [isChecking, setIsChecking] = useState(true);
  const [, setSearchParams] = useSearchParams();
  const { clientId } = useClientConfig();

  useEffect(() => {
    let verificationTimer: NodeJS.Timeout;
    let progressTimer: NodeJS.Timeout;
    let timeoutTimer: NodeJS.Timeout;
    let completionTimer: NodeJS.Timeout;

    const checkVerificationStatus = async () => {
      try {
        const response = await getUserVerified();

        if (response.isVerified) {
          handleVerificationSuccess();
        }
      } catch (error) {
        console.log('WebID verification check error, continuing to check...', error);
      }
    };

    const updateProgress = () => {
      setProgress((prevProgress) => Math.min(prevProgress + PROGRESS_INCREMENT, MAX_PROGRESS_WHILE_CHECKING));
    };

    const handleVerificationSuccess = () => {
      setProgress(100);
      cleanup();
      completionTimer = setTimeout(() => {
        setIsChecking(false);
        trackEvent('is24.de.sicherheit.verifizierung.webid', 'verification_successful', 'user', clientId, 'webid');
        setSearchParams({ tvs_state: 'verification_success' });
        onVerificationComplete?.(VerificationStatus.SUCCESS);
      }, COMPLETION_DELAY_MS);
    };

    const handleVerificationTimeout = () => {
      setProgress(100);
      cleanup();
      completionTimer = setTimeout(() => {
        setIsChecking(false);
        setSearchParams({ tvs_state: 'verification_failure_webid' });
        onVerificationComplete?.(VerificationStatus.FAILED);
      }, COMPLETION_DELAY_MS);
    };

    const cleanup = () => {
      clearTimeout(timeoutTimer);
      clearInterval(progressTimer);
      clearInterval(verificationTimer);
    };

    if (isChecking) {
      timeoutTimer = setTimeout(handleVerificationTimeout, MAX_VERIFICATION_TIME_MS);
      verificationTimer = setInterval(checkVerificationStatus, POLLING_INTERVAL_MS);
      progressTimer = setInterval(updateProgress, POLLING_INTERVAL_MS);
      checkVerificationStatus();
      return () => {
        cleanup();
        clearTimeout(completionTimer);
      };
    }
  }, [isChecking, onVerificationComplete, setSearchParams]);

  if (!isChecking) return null;

  return <VerificationStatusCheck progress={progress} verificationMethod={VerificationMethod.WebId} />;
};

export default WebIdStatusCheckPage;
